/**
 * Resolución + persistencia del CONTEXTO de builder.
 *
 * El contexto viaja en la URL (`?veoBuilder=<token>&veoBuilderOrigin=<origen>…`),
 * pero los guards de auth de la app suelen **redirigir y tirar el query string**
 * (p.ej. `/dashboard/home` → `/` para loguearte). Si dependiéramos solo de la URL,
 * el modo builder moriría en ese redirect y nunca podrías diseñar sobre una página
 * detrás de login.
 *
 * Solución (igual que el "designer mode" de Pendo): apenas vemos el token en la
 * URL guardamos el contexto en `sessionStorage` y de ahí en más lo leemos como
 * fallback. Así el modo builder **sobrevive a redirects y a un login** dentro de la
 * misma pestaña (incluido el ida-y-vuelta de OAuth). Persiste hasta el teardown o
 * hasta que se cierra la pestaña.
 *
 * Vive en el core (bundle de producción) porque tanto el loader del builder como
 * el gate del runtime de guías necesitan saber si estamos en modo builder. Es
 * mínimo a propósito (ver budget en CLAUDE.md).
 */
import { hasWindow } from '../utils/safe-env';
import {
  BUILDER_GUIDE_PARAM,
  BUILDER_ORIGIN_PARAM,
  BUILDER_PANEL_PARAM,
  BUILDER_PANEL_PATH_PARAM,
  BUILDER_TOKEN_PARAM,
} from './builder-constants';
import { isPreviewMode } from './preview-loader';

// Re-export para no romper a quien los importa desde aquí (bridge-protocol, etc.).
export { BUILDER_ORIGIN_PARAM, BUILDER_TOKEN_PARAM } from './builder-constants';

/** Contexto de la sesión de builder activa en la app. */
export interface BuilderClientContext {
  /** Token de correlación del handshake. */
  token: string;
  /** Origen del dashboard de Veo (para postMessage). */
  origin: string | null;
  /** `true` → modo panel inyectado (app top-level, editor encima). */
  panel: boolean;
  /** Id de la guía en edición (para el panel). */
  guide: string | null;
  /** Path del panel del editor en el dashboard (override; default `/guides/design`). */
  panelPath: string | null;
}

const CTX_KEY = 'veo:builder_ctx';
/**
 * Respaldo en localStorage, KEYED POR GUÍA. sessionStorage muere al cerrar la
 * pestaña; para que la sesión del editor "perdure" al abrir varias guías o
 * reabrir la misma, además persistimos por guía en localStorage con un TTL.
 * Keyed por guía → dos guías abiertas en pestañas distintas no se pisan.
 */
const LS_PREFIX = 'veo:builder_ctx:';
/**
 * TTL del respaldo. Antes era 12 h y producía "sesiones fantasma": cerrabas la
 * pestaña del editor sin teardown y CUALQUIER visita a la app durante 12 h
 * restauraba un contexto viejo (token/plantilla stale) o re-abría el panel.
 * 30 min alcanza para su propósito real — sobrevivir el ida-y-vuelta del login
 * y recuperar un cierre accidental — sin arrastrar sesiones de ayer.
 */
const PERSIST_TTL_MS = 30 * 60 * 1000; // 30 min

interface StoredCtx extends BuilderClientContext {
  savedAt: number;
}

function session(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null; // sessionStorage puede tirar (modo privado, sandbox)
  }
}

function local(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function lsKey(guide: string | null): string {
  return LS_PREFIX + (guide || 'default');
}

/** Persiste el contexto en sessionStorage (pestaña) + localStorage por guía (TTL). */
function persist(ctx: BuilderClientContext): void {
  try {
    session()?.setItem(CTX_KEY, JSON.stringify(ctx));
  } catch {
    // no-op: sin sessionStorage seguimos con el contexto de la URL
  }
  try {
    const stored: StoredCtx = { ...ctx, savedAt: Date.now() };
    local()?.setItem(lsKey(ctx.guide), JSON.stringify(stored));
  } catch {
    // no-op
  }
}

/** Contexto más reciente no vencido en localStorage (limpia los vencidos de paso). */
function readFreshFromLocal(): BuilderClientContext | null {
  const store = local();
  if (!store) return null;
  let best: StoredCtx | null = null;
  try {
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (!key?.startsWith(LS_PREFIX)) continue;
      const raw = store.getItem(key);
      if (!raw) continue;
      let parsed: StoredCtx | null = null;
      try {
        parsed = JSON.parse(raw) as StoredCtx;
      } catch {
        continue;
      }
      if (typeof parsed?.savedAt !== 'number' || Date.now() - parsed.savedAt > PERSIST_TTL_MS) {
        try {
          store.removeItem(key);
        } catch {
          // no-op
        }
        continue;
      }
      if (!best || parsed.savedAt > best.savedAt) best = parsed;
    }
  } catch {
    return null;
  }
  if (!best) return null;
  const { savedAt: _savedAt, ...ctx } = best;
  return ctx;
}

function urlParams(): URLSearchParams | null {
  try {
    const search = new URLSearchParams(window.location.search);
    if (search.get(BUILDER_TOKEN_PARAM)) return search;
    // Fallback: FRAGMENTO (#veoBuilder=…). El dashboard duplica los params en el
    // hash porque, con sesión iniciada en la app, el guard de auth suele
    // redirigir la raíz y el redirect tira el query string — pero los
    // navegadores preservan el fragmento a través de los 3xx. Sin esto, el
    // editor solo abría con el usuario deslogueado.
    const hash = window.location.hash.replace(/^#/, '');
    if (hash.includes(`${BUILDER_TOKEN_PARAM}=`)) return new URLSearchParams(hash);
    return search;
  } catch {
    return null;
  }
}

/**
 * Contexto de builder activo: el de la URL (que además se persiste) o, si el
 * query string se perdió en un redirect, el guardado en sessionStorage. `null`
 * si no estamos en modo builder.
 */
export function resolveBuilderContext(): BuilderClientContext | null {
  if (!hasWindow()) return null;
  const params = urlParams();
  const fromUrl = params?.get(BUILDER_TOKEN_PARAM);
  if (fromUrl) {
    const ctx: BuilderClientContext = {
      token: fromUrl,
      origin: params?.get(BUILDER_ORIGIN_PARAM) ?? null,
      panel: params?.get(BUILDER_PANEL_PARAM) === '1',
      guide: params?.get(BUILDER_GUIDE_PARAM) ?? null,
      panelPath: params?.get(BUILDER_PANEL_PATH_PARAM) ?? null,
    };
    persist(ctx);
    return ctx;
  }
  // Preview-por-link (`?veoPreview`) es un modo EXCLUYENTE del builder: si la app
  // corre en preview NO revivimos una sesión de builder desde la persistencia.
  // Sin esto, abrir un link de preview dentro de los 30 min de haber editado
  // resucitaba el contexto de localStorage y RE-INYECTABA el editor ENCIMA del
  // preview — y ese remonte podía tirar el trabajo sin guardar. Un `?veoBuilder`
  // explícito en la URL sí gana (se resolvió arriba, antes de este chequeo).
  if (isPreviewMode()) return null;
  // Sin token en la URL: primero la pestaña (sessionStorage), y si murió (pestaña
  // nueva / restaurada) caemos al respaldo persistido más reciente y lo
  // re-hidratamos en la sesión para que el resto de la carga sea consistente.
  try {
    const raw = session()?.getItem(CTX_KEY);
    if (raw) return JSON.parse(raw) as BuilderClientContext;
  } catch {
    // sigue al fallback de localStorage
  }
  const fromLocal = readFreshFromLocal();
  if (fromLocal) {
    try {
      session()?.setItem(CTX_KEY, JSON.stringify(fromLocal));
    } catch {
      // no-op
    }
  }
  return fromLocal;
}

/** Token de builder activo (URL o persistido). */
export function resolveBuilderToken(): string | null {
  return resolveBuilderContext()?.token ?? null;
}

/** Origen del dashboard (URL o persistido). */
export function resolveBuilderOrigin(): string | null {
  return resolveBuilderContext()?.origin ?? null;
}

/** `true` si hay un contexto de builder activo (URL o persistido). */
export function isBuilderMode(): boolean {
  return resolveBuilderContext() !== null;
}

/**
 * Borra el modo builder persistido (al hacer teardown / salir del editor).
 * Limpia la pestaña (sessionStorage) y el respaldo de ESTA guía (localStorage);
 * los respaldos de otras guías siguen (no se pisan al cerrar una).
 */
export function clearBuilderToken(): void {
  if (!hasWindow()) return;
  let guide: string | null = null;
  try {
    const raw = session()?.getItem(CTX_KEY);
    if (raw) guide = (JSON.parse(raw) as BuilderClientContext).guide ?? null;
  } catch {
    // no-op
  }
  try {
    session()?.removeItem(CTX_KEY);
  } catch {
    // no-op
  }
  try {
    local()?.removeItem(lsKey(guide));
  } catch {
    // no-op
  }
}
